import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@repo/ui";
import type { Echelle } from "@convex/referentiel/fourchette";
import { CarteDimension } from "./carte-dimension";
import {
  estComplete,
  etatInitial,
  prochaineIncomplete,
  SECTIONS,
  versReponses,
  type ConfigReferentiel,
  type ReponseFourchette,
  type ValeurDimension,
} from "./fourchettes-modele";
import { NavigationDimensions } from "./navigation-dimensions";
import { RecapitulatifFourchettes } from "./recapitulatif-fourchettes";

export type { ReponseFourchette } from "./fourchettes-modele";

type Props = {
  config: ConfigReferentiel;
  brouillon: unknown;
  lectureSeule: boolean;
  enCoursEnvoi: boolean;
  onBrouillon: (reponses: ReponseFourchette[]) => void;
  onValider: (reponses: ReponseFourchette[]) => void;
};

const estChampDeSaisie = (cible: EventTarget | null) =>
  cible instanceof HTMLElement &&
  (cible.isContentEditable || ["INPUT", "SELECT", "TEXTAREA"].includes(cible.tagName));

/**
 * Saisie du référentiel PAMI en CARROUSEL : une dimension à la fois, puis un
 * récapitulatif (position `dimensions.length`) qui porte la validation.
 *
 * Aucun passage automatique : choisir une attente ne change pas de carte. Le
 * référent lit la définition, la cloche, change d'avis s'il veut, puis passe à
 * la suite avec « Suivant » (mis en avant dès que la dimension est
 * renseignée) ou les pastilles. Les flèches du clavier naviguent aussi, sauf
 * dans un groupe de radios où elles changent le choix.
 *
 * Reprise d'un brouillon : sur la première dimension incomplète. Contribution
 * validée (lecture seule) : directement sur le récapitulatif.
 */
export function ReferentielFourchettes({
  config,
  brouillon,
  lectureSeule,
  enCoursEnvoi,
  onBrouillon,
  onValider,
}: Props) {
  const dimensions = config.dimensions;
  const total = dimensions.length;
  const echelle: Echelle =
    config.echelle.kind === "fourchette" ? config.echelle : { min: 0, max: 10 };

  const [etat, setEtat] = useState(() => etatInitial(config, brouillon));
  const [position, setPosition] = useState(() =>
    lectureSeule ? total : prochaineIncomplete(dimensions, etat, -1),
  );
  const [sens, setSens] = useState<"avant" | "arriere">("avant");

  const zoneRef = useRef<HTMLDivElement>(null);
  const titreRef = useRef<HTMLHeadingElement>(null);
  const premierRendu = useRef(true);

  const aller = useCallback(
    (cible: number) => {
      setSens(cible >= position ? "avant" : "arriere");
      setPosition(cible);
    },
    [position],
  );

  // Flèches du clavier.
  useEffect(() => {
    const surTouche = (evenement: KeyboardEvent) => {
      if (estChampDeSaisie(evenement.target) || evenement.altKey || evenement.metaKey)
        return;
      if (evenement.key === "ArrowRight" && position < total) aller(position + 1);
      else if (evenement.key === "ArrowLeft" && position > 0) aller(position - 1);
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [aller, position, total]);

  // La carte qui arrive prend le focus (lecteurs d'écran, clavier) et revient
  // dans l'écran si la page avait défilé plus bas.
  useEffect(() => {
    if (premierRendu.current) {
      premierRendu.current = false;
      return;
    }
    titreRef.current?.focus({ preventScroll: true });
    const zone = zoneRef.current;
    if (zone && zone.getBoundingClientRect().top < 0) {
      const reduit = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      zone.scrollIntoView({ block: "start", behavior: reduit ? "auto" : "smooth" });
    }
  }, [position]);

  const modifier = (code: string, valeur: ValeurDimension) => {
    const suivant = { ...etat, [code]: valeur };
    setEtat(suivant);
    onBrouillon(versReponses(suivant));
  };

  const surRecapitulatif = position === total;
  const dimension = dimensions[position];
  const categorie = dimension?.categorie ?? "";
  const section = SECTIONS[categorie];
  const membresSection = dimensions.filter((d) => (d.categorie ?? "") === categorie);
  const rangDansSection = membresSection.findIndex((d) => d.code === dimension?.code);

  return (
    <div className="space-y-5 pt-2">
      <NavigationDimensions
        dimensions={dimensions}
        etat={etat}
        position={position}
        onAller={aller}
      />

      <div ref={zoneRef} className="scroll-mt-4 space-y-3">
        {dimension && section && (
          <header>
            <h3 className="text-xl font-semibold text-charte-strong">
              {section.titre}
              <span className="ml-2 whitespace-nowrap text-base font-normal tabular-nums text-charte-dim">
                {rangDansSection + 1} / {membresSection.length}
              </span>
            </h3>
            {section.introduction && (
              <p className="mt-1 text-sm text-charte-muted">{section.introduction}</p>
            )}
          </header>
        )}

        {/* La carte arrive en glissant depuis le côté où l'on va. Seule
            l'entrée est animée : la carte sortante disparaît aussitôt, ce qui
            évite deux cartes à l'écran et un saut de hauteur. */}
        <div className="-mx-2 overflow-x-clip px-2 pb-2">
          <div
            key={position}
            className={`animate-in fade-in-0 duration-300 ease-out motion-reduce:animate-none ${
              sens === "avant" ? "slide-in-from-right-24" : "slide-in-from-left-24"
            }`}
          >
            {dimension ? (
              <CarteDimension
                dimension={dimension}
                valeur={etat[dimension.code] ?? {}}
                echelle={echelle}
                lectureSeule={lectureSeule}
                titreRef={titreRef}
                onModifier={(valeur) => modifier(dimension.code, valeur)}
              />
            ) : (
              <RecapitulatifFourchettes
                dimensions={dimensions}
                etat={etat}
                echelle={echelle}
                lectureSeule={lectureSeule}
                enCoursEnvoi={enCoursEnvoi}
                titreRef={titreRef}
                onAller={aller}
                onValider={() => onValider(versReponses(etat))}
              />
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            disabled={position === 0}
            onClick={() => aller(position - 1)}
          >
            <ArrowLeft aria-hidden className="mr-2 h-4 w-4" />
            Précédent
          </Button>
          {!surRecapitulatif && (
            <Button
              variant={estComplete(etat[dimension?.code ?? ""]) ? "default" : "outline"}
              onClick={() => aller(position + 1)}
            >
              {position === total - 1 ? "Récapitulatif" : "Suivant"}
              <ArrowRight aria-hidden className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
